import {
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';

import {Fragment} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {
  useGetUserDashboardSettingsQuery,
  usePutUserDashboardSettingsMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {handleApiError, useIsLicenceEnabled, usePermissions} from '@dms/shared';

import {ApiError} from 'shared/web';

import {MyProfileFooter} from '../../../components/MyProfileFooter';
import {DashboardFormSchema, DashboardFormSchemaType} from '../schemas/DashboardFormSchema';
import {getWorkshopLeafKeys} from '../utils/getWorkshopLeafKeys';
import {getWorkshopSettingsPayload} from '../utils/getWorkshopSettingsPayload';
import {reconcileWorkshopParents} from '../utils/reconcileWorkshopParents';
import {WORKSHOP_WIDGET_GROUPS, WorkshopGroupKey} from '../utils/workshopWidgetGroups';

const getLabel = (key: string) => i18n.t(`page.myProfile.dashboard.labels.${key}`);

// eslint-disable-next-line complexity -- Pre-existing violation
export function DashboardForm() {
  const [updateUserDashboardSettings] = usePutUserDashboardSettingsMutation();
  const {
    data: dashboardSettings,
    isLoading: isDashboardSettingsLoading,
    isError: hasDashboardError,
  } = useGetUserDashboardSettingsQuery();

  const [canReadTasks] = usePermissions({
    permissionKeys: ['readTask'],
  });

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();

  const handleSubmit: FormSubmitHandler<DashboardFormSchemaType> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await updateUserDashboardSettings({
      salesFunnelInterests: values.salesFunnelInterests,
      salesFunnelBusinessCases: values.salesFunnelBusinessCases,
      vehicleStock: values.vehicleStock,
      latestVehicles: values.latestVehicles,
      latestBusinessCases: values.latestBusinessCases,
      latestInterests: values.latestInterests,
      notifications: values.notifications,
      myTasks: values.myTasks,
      ...getWorkshopSettingsPayload(values, isWorkshopModuleEnabled),
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const onChangeHasSalesFunnel = (
    hasSalesFunnel: boolean,
    formApi: UseFormReturn<DashboardFormSchemaType>
  ) => {
    formApi.setValue('salesFunnelInterests', hasSalesFunnel);
    formApi.setValue('salesFunnelBusinessCases', hasSalesFunnel);
  };

  const onChangeHasSalesFunnelChild = (formApi: UseFormReturn<DashboardFormSchemaType>) => {
    const values = formApi.getValues();

    if (!values.salesFunnelInterests && !values.salesFunnelBusinessCases) {
      formApi.setValue('hasSalesFunnel', false);
    }
  };

  const reconcileWorkshop = (formApi: UseFormReturn<DashboardFormSchemaType>) => {
    const parents = reconcileWorkshopParents(formApi.getValues());
    formApi.setValue('workshopOverview', parents.workshopOverview);
    formApi.setValue('workshopCritical', parents.workshopCritical);
    formApi.setValue('workshopWarning', parents.workshopWarning);
    formApi.setValue('workshopToday', parents.workshopToday);
  };

  const onChangeWorkshopAggregate = (
    target: 'overview' | WorkshopGroupKey,
    value: boolean,
    formApi: UseFormReturn<DashboardFormSchemaType>
  ) => {
    getWorkshopLeafKeys(target).forEach((leaf) => formApi.setValue(leaf, value));
    reconcileWorkshop(formApi);
  };

  const workshopLeafDefaults = {
    workshopVehiclesNotArrived: dashboardSettings?.workshopVehiclesNotArrived ?? false,
    workshopOverpricedOrders: dashboardSettings?.workshopOverpricedOrders ?? false,
    workshopOverdueOrders: dashboardSettings?.workshopOverdueOrders ?? false,
    workshopOrdersBelowMinimumMargin: dashboardSettings?.workshopOrdersBelowMinimumMargin ?? false,
    workshopUnclosedWarrantyClaims: dashboardSettings?.workshopUnclosedWarrantyClaims ?? false,
    workshopOrdersWithoutDateOrPrice: dashboardSettings?.workshopOrdersWithoutDateOrPrice ?? false,
    workshopRepeatedRepairs: dashboardSettings?.workshopRepeatedRepairs ?? false,
    workshopToCompleteToday: dashboardSettings?.workshopToCompleteToday ?? false,
    workshopTodaysVehicleArrivals: dashboardSettings?.workshopTodaysVehicleArrivals ?? false,
  };

  const defaultValues = {
    salesFunnelBusinessCases: dashboardSettings?.salesFunnelBusinessCases ?? false,
    salesFunnelInterests: dashboardSettings?.salesFunnelInterests ?? false,
    hasSalesFunnel:
      (dashboardSettings?.salesFunnelBusinessCases || dashboardSettings?.salesFunnelInterests) ??
      false,
    vehicleStock: dashboardSettings?.vehicleStock ?? false,
    latestVehicles: dashboardSettings?.latestVehicles ?? false,
    latestBusinessCases: dashboardSettings?.latestBusinessCases ?? false,
    latestInterests: dashboardSettings?.latestInterests ?? false,
    notifications: dashboardSettings?.notifications ?? false,
    myTasks: dashboardSettings?.myTasks ?? false,
    ...workshopLeafDefaults,
    ...reconcileWorkshopParents(workshopLeafDefaults),
  };

  return (
    <Box maxWidth={200}>
      <DataStatus isLoading={isDashboardSettingsLoading} isError={hasDashboardError}>
        <Form<DashboardFormSchemaType>
          shouldWatchForUnsavedChanges
          defaultValues={defaultValues}
          experimentalZodSchema={DashboardFormSchema}
          onSubmit={handleSubmit}
        >
          {(control, formApi) => {
            const hasSalesFunnel = formApi.watch('hasSalesFunnel');
            const workshopOverview = formApi.watch('workshopOverview');

            return (
              <>
                <VStack spacing={6}>
                  <Text color="tertiary" size="small">
                    {i18n.t('page.myProfile.dashboard.sections.general')}
                  </Text>

                  <FormField
                    control={control}
                    name="notifications"
                    label={getLabel('notifications')}
                    type="switch"
                  />

                  <Show when={canReadTasks}>
                    <FormField
                      control={control}
                      name="myTasks"
                      label={getLabel('showMyTasks')}
                      type="switch"
                    />
                  </Show>

                  <Text color="tertiary" size="small">
                    {i18n.t('page.myProfile.dashboard.sections.sales')}
                  </Text>

                  <FormField
                    control={control}
                    name="hasSalesFunnel"
                    label={getLabel('opportunitiesFunnel')}
                    type="switch"
                    onChange={(value) => onChangeHasSalesFunnel(value, formApi)}
                  />

                  <Box paddingLeft={12}>
                    <VStack spacing={6}>
                      <FormField
                        control={control}
                        name="salesFunnelInterests"
                        label={getLabel('interests')}
                        type="switch"
                        isDisabled={!hasSalesFunnel}
                        onChange={() => onChangeHasSalesFunnelChild(formApi)}
                      />
                      <FormField
                        control={control}
                        name="salesFunnelBusinessCases"
                        label={getLabel('businessCases')}
                        type="switch"
                        isDisabled={!hasSalesFunnel}
                        onChange={() => onChangeHasSalesFunnelChild(formApi)}
                      />
                    </VStack>
                  </Box>

                  <FormField
                    control={control}
                    name="vehicleStock"
                    label={getLabel('vehicleStockOverview')}
                    type="switch"
                  />

                  <FormField
                    control={control}
                    name="latestVehicles"
                    label={getLabel('latestVehicles')}
                    type="switch"
                  />

                  <FormField
                    control={control}
                    name="latestBusinessCases"
                    label={getLabel('latestBusinessCases')}
                    type="switch"
                  />

                  <FormField
                    control={control}
                    name="latestInterests"
                    label={getLabel('latestInterests')}
                    type="switch"
                  />

                  <Show when={isWorkshopModuleEnabled}>
                    <Text color="tertiary" size="small">
                      {i18n.t('page.myProfile.dashboard.sections.workshop')}
                    </Text>

                    <FormField
                      control={control}
                      name="workshopOverview"
                      label={getLabel('workshopOverview')}
                      type="switch"
                      onChange={(value) => onChangeWorkshopAggregate('overview', value, formApi)}
                    />

                    <Box paddingLeft={12}>
                      <VStack spacing={6}>
                        {WORKSHOP_WIDGET_GROUPS.map((group) => {
                          const isGroupEnabled = formApi.watch(group.groupKey);

                          return (
                            <Fragment key={group.groupKey}>
                              <FormField
                                control={control}
                                name={group.groupKey}
                                label={getLabel(group.labelKey)}
                                type="switch"
                                isDisabled={!workshopOverview}
                                onChange={(value) =>
                                  onChangeWorkshopAggregate(group.groupKey, value, formApi)
                                }
                              />

                              <Box paddingLeft={12}>
                                <VStack spacing={6}>
                                  {group.widgets.map((widget) => (
                                    <FormField
                                      key={widget.key}
                                      control={control}
                                      name={widget.key}
                                      label={getLabel(widget.labelKey)}
                                      type="switch"
                                      isDisabled={!isGroupEnabled}
                                      onChange={() => reconcileWorkshop(formApi)}
                                    />
                                  ))}
                                </VStack>
                              </Box>
                            </Fragment>
                          );
                        })}
                      </VStack>
                    </Box>
                  </Show>
                </VStack>
                <MyProfileFooter
                  actions={[
                    {
                      type: 'button',
                      onClick: () => formApi.reset(defaultValues),
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: i18n.t('general.actions.saveChanges'),
                    },
                  ]}
                />
              </>
            );
          }}
        </Form>
      </DataStatus>
    </Box>
  );
}
